/**
 * To allow components to be interactive, we need to track their coordinates relative to the
 * shape the components are being rendered on. To simplify this for now, we will proxy the
 * 2d context and track calls to `translate` so we know its relative position. This position
 * is then used to handle mousemove and click events.
 */
class CanvaSXRenderingContext2D {
  currentPosition: PositionStruct | undefined;
  positionStack: PositionStruct[];

  currentScale: ScaleStruct | undefined;
  scaleStack: ScaleStruct[];

  _proxied: CanvasRenderingContext2D;

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  get proxied(): CanvasRenderingContext2D & CanvaSXRenderingContext2D {
    // Create a proxy to the Canvas context
    // @ts-expect-error the proxy overwrites some canvas methods, but also adds a few extra methods
    return (this._proxied ||= new Proxy(this.ctx, {
      get: (target, prop) => {
        const obj = prop in this ? this : target;

        const value = obj[prop];
        if (typeof value === 'function') {
          return value.bind(obj);
        } else {
          return value;
        }
      },
      set: (target, prop, value) => {
        if (prop in this) {
          this[prop] = value;
        } else {
          target[prop] = value;
        }

        return value;
      },
      has: (target, prop) => {
        return target.hasOwnProperty(prop);
      },
    }));
  }

  get position(): PositionStruct {
    return this.currentPosition || { x: 0, y: 0 };
  }

  get currentScaleStruct() {
    return this.currentScale || { scaleX: 1, scaleY: 1 };
  }

  translate(x: number, y: number): void {
    // Create new position so we don't mutate the position in the stack
    this.currentPosition = {
      x: this.position.x + x,
      y: this.position.y + y,
    };

    this.ctx.translate(x, y);
  }

  scale(x: number, y: number): void {
    this.currentScale = {
      scaleX: this.currentScaleStruct.scaleX * x,
      scaleY: this.currentScaleStruct.scaleY * y,
    };

    this.ctx.scale(x, y);
  }

  save(): void {
    this.positionStack ||= [];
    this.positionStack.push(this.position);

    this.scaleStack ||= [];
    this.scaleStack.push(this.currentScaleStruct);

    this.ctx.save();
  }

  restore(): void {
    this.currentPosition = this.positionStack.pop();
    this.currentScale = this.scaleStack.pop();

    this.ctx.restore();
  }
}

export function proxyCanvas(ctx: CanvasRenderingContext2D) {
  const context = new CanvaSXRenderingContext2D(ctx);

  return context.proxied;
}
